import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Button,
  Snackbar,
  Alert,
  Badge,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Groups as NetworkingIcon,
  WorkOutline as JobAppsIcon,
  Sensors as SignalsIcon,
} from '@mui/icons-material'
import { signOut } from 'aws-amplify/auth'
import { userAtom } from '../../state/auth'
import { notificationAtom } from '../../state/notifications'
import { awaitingDecisionJobsAtom } from '../../state/jobOpportunities'
import { colors } from '../../theme'
import GlobalPortalNotifications from './GlobalPortalNotifications'
import PortalUnavailable from './PortalUnavailable'
import { usePortalData } from './PortalDataProvider'
import BrandLogo from '../BrandLogo'
import BackButton from '../common/BackButton'

const drawerWidth = 260

// Runtime environment detection based on hostname (matches aws.ts pattern)
const getEmailDomain = () => {
  const hostname = window.location.hostname
  const isDevEnvironment = hostname.includes('dev.') || hostname.includes('localhost') || hostname === 'localhost'
  return isDevEnvironment ? 'devmail.missfitcoaching.com' : 'email.missfitcoaching.com'
}

const contactEmail = `contact@${getEmailDomain()}`
const contactPhone = '(872) 224-2803'

interface LayoutProps {
  children: React.ReactNode
}

interface NavItem {
  text: string
  icon: React.ReactNode
  path: string
}

const navItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/portal' },
  { text: 'My Profile', icon: <PersonIcon />, path: '/portal/profile' },
  { text: 'Side Door', icon: <NetworkingIcon />, path: '/portal/networking' },
  { text: 'Front Door', icon: <JobAppsIcon />, path: '/portal/opportunities' },
  // MWA-421 — gated with the Front Door (see portalAlsoPaths in stageVisibility).
  { text: 'Signals', icon: <SignalsIcon />, path: '/portal/signals' },
]

export default function PortalLayout({ children }: LayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [user] = useAtom(userAtom)
  const { visibility } = usePortalData()
  const [notification, setNotification] = useAtom(notificationAtom)
  const [awaitingDecisionJobs] = useAtom(awaitingDecisionJobsAtom)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleNotificationClose = () => {
    setNotification(null)
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  // One list for both Drawers and the job-applications badge, so the stage's
  // configuration reaches every copy of the nav from here.
  const visibleNavItems = navItems.filter((item) => visibility.isPathVisible(item.path))

  const drawer = (
    <Box>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
        {/* Lockup carries the wordmark; only the "Client Portal" context label remains */}
        <BrandLogo height={34} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Client Portal
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ px: 2, py: 2 }}>
        {visibleNavItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.05)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
              {item.path === '/portal/opportunities' && awaitingDecisionJobs.length > 0 && (
                <Badge
                  badgeContent={awaitingDecisionJobs.length}
                  color="error"
                  sx={{ mr: 1.5 }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      {/* MWA-220 — global portal WS listener (keeps job data fresh, fires toasts) */}
      <GlobalPortalNotifications />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <BackButton home="/portal" />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={handleMenuClick} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: colors.primary, color: 'white' }}>
              {user?.name?.[0] || user?.email?.[0]?.toUpperCase() || 'C'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{user?.name || user?.email}</Typography>
              <Typography variant="caption" color="text.secondary">
                Client Portal
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          // Below sm there is no width above, so as a flex item main would
          // otherwise grow to its widest content — a scrollable tab strip's full
          // length — and widen the whole page instead of letting the strip scroll.
          minWidth: 0,
          mt: 8,
          backgroundColor: 'background.default',
          // See Layout.tsx — subtracting AppBar height keeps body at 100vh
          // exactly, so kanban pages don't get spurious y-axis overflow (MWA-209).
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Box sx={{ p: 3, flexGrow: 1, minWidth: 0 }}>
          {/* Gating here rather than per page means a portal page added later is
              covered by construction, and a hidden page never mounts, so it fires
              no requests. Rendering in place rather than redirecting is what
              keeps this and ProtectedPortalRoute from fighting. */}
          {visibility.isPathVisible(location.pathname) ? children : <PortalUnavailable />}
        </Box>

        {/* The contact footer, at the end of the content rather than pinned to
            the viewport. Pinned, it reserved 160px of every page (main carried
            pb: 20 to clear it) — a fifth of a 844px phone screen, permanently,
            for an email address. */}
        <Box
          component="footer"
          sx={{
            flexShrink: 0,
            pt: 3,
            pb: 2,
            px: 3,
            borderTop: `1px solid ${colors.gray200}`,
            backgroundColor: colors.gray100,
            textAlign: 'center',
          }}
        >
        <BrandLogo variant="glyph" height={36} sx={{ mx: 'auto', mb: 1, borderRadius: 1 }} />
        <Typography variant="body2" sx={{ color: colors.gray600, mb: 1.5 }}>
          Questions on a document? Reply here and name it. We’ll cover it on your next call
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray600 }}>
          <Box
            component="a"
            href={`mailto:${contactEmail}`}
            sx={{ color: colors.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {contactEmail}
          </Box>
          {' · '}
          <Box
            component="a"
            href="tel:+18722242803"
            sx={{ color: colors.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {contactPhone}
          </Box>
        </Typography>
        </Box>
      </Box>

      {/* Global Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={notification?.autoHideDuration ?? 4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%', color: 'white' }}
          // The staff shell has always rendered notification.action; this one
          // did not, so an actionable notification raised from a portal page —
          // the Undo on Copy & Log, for one — showed its message with no way
          // to act on it.
          action={
            notification?.action ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  notification.action?.onClick()
                  handleNotificationClose()
                }}
              >
                {notification.action.label}
              </Button>
            ) : undefined
          }
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
